import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { ChevronDown, Sparkles } from "lucide-react";
import supersub from "remark-supersub";
import remarkGfm from "remark-gfm";
import remarkMath from "remark-math";
import rehypeRaw from "rehype-raw";
import rehypeSanitize from "rehype-sanitize";
import rehypeKatex from "rehype-katex";

import "./Answer.css";

import { CodeBlock } from "@/components/Markdown/CodeBlock";
import { MemoizedReactMarkdown } from "@/components/Markdown/MemoizedReactMarkdown";

/* Delay before a live trace appears. Turns that resolve faster than this
   render nothing at all, rather than flashing an indicator for a few frames. */
const APPEAR_DELAY_MS = 300;

interface ThinkingTraceProps {
  /** Streamed reasoning summary, as markdown. */
  reasoning?: string;
  /** True while the trace is still advancing. */
  working: boolean;
  activeLabel: string;
  /** Label once settled. Receives elapsed seconds when it contains "{s}". */
  settledLabel: string;
}

const ThinkingTrace = ({ reasoning, working, activeLabel, settledLabel }: ThinkingTraceProps) => {
  const scrollRef = useRef<HTMLDivElement>(null);
  const traceRef = useRef<HTMLDivElement>(null);
  const startedAtRef = useRef<number>(Date.now());
  const [manualExpanded, setManualExpanded] = useState<boolean | null>(null);
  const [lineHeight, setLineHeight] = useState(0);
  const [appeared, setAppeared] = useState(!working);
  const [elapsedSeconds, setElapsedSeconds] = useState<number | null>(null);

  const hasReasoning = Boolean(reasoning && reasoning.trim());
  const autoExpanded = working && hasReasoning;
  const expanded = manualExpanded ?? autoExpanded;

  /* Hold the trace back briefly so fast turns never flash it. */
  useEffect(() => {
    if (appeared) return;
    const timer = setTimeout(() => setAppeared(true), APPEAR_DELAY_MS);
    return () => clearTimeout(timer);
  }, [appeared]);

  /* Freeze the elapsed time at the moment the trace settles. */
  useEffect(() => {
    if (working || elapsedSeconds !== null) return;
    setElapsedSeconds(Math.max(1, Math.round((Date.now() - startedAtRef.current) / 1000)));
  }, [working, elapsedSeconds]);

  /* Follow the reasoning as it streams. */
  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [reasoning, expanded]);

  /* The connector line is drawn to the measured height of the reasoning. */
  /* `appeared` gates an early return, so the ref is not attached on the first
     layout pass -- it has to be a dependency here or the line stays at 0. */
  useLayoutEffect(() => {
    if (traceRef.current) setLineHeight(traceRef.current.offsetHeight);
  }, [reasoning, expanded, appeared]);

  if (!appeared) return null;

  const resolvedSettledLabel = settledLabel.replace("{s}", String(elapsedSeconds ?? 1));

  return (
    <div className="flex w-full flex-col">
      <button
        type="button"
        aria-expanded={expanded}
        onClick={() => setManualExpanded((current) => !(current ?? autoExpanded))}
        className="-mx-1.5 flex w-fit items-center gap-2 rounded-control px-1.5 py-1 transition-colors duration-100 hover:bg-hover-2"
      >
        <Sparkles size={15} className={working ? "text-ink-2" : "text-ink-3"} />
        <span role="status" className="contents">
          {working ? (
            <span className="trace-shimmer text-[13px] font-medium whitespace-nowrap">{activeLabel}</span>
          ) : (
            <span className="text-[13px] font-medium whitespace-nowrap text-ink-2" style={{ animation: "fade-in 350ms ease-out both" }}>
              {resolvedSettledLabel}
            </span>
          )}
        </span>
        {hasReasoning && (
          <ChevronDown
            size={14}
            className="text-ink-3 transition-transform duration-300"
            style={{ transform: expanded ? "rotate(180deg)" : "rotate(0)" }}
          />
        )}
      </button>

      <div
        className="grid transition-[grid-template-rows,opacity] duration-300"
        style={{
          gridTemplateRows: expanded ? "1fr" : "0fr",
          opacity: expanded ? 1 : 0,
          transitionTimingFunction: "cubic-bezier(0.23, 1, 0.32, 1)",
        }}
      >
        <div className="overflow-hidden">
          <div className="relative mt-1 ml-[5px] pl-4">
            <span
              aria-hidden
              className="absolute left-[3px] w-px bg-line"
              style={{ top: -8, height: lineHeight ? lineHeight - 2 : 0, transition: "height 500ms cubic-bezier(0.23,1,0.32,1)" }}
            />
            <div ref={traceRef} className="flex flex-col gap-1 py-1">
              {hasReasoning && (
                <div ref={scrollRef} className="max-h-[300px] overflow-y-auto scrollbar-none px-1.5">
                  <div className="model-response markdown-body prose dark:prose-invert max-w-full bg-transparent text-[12.5px] leading-relaxed [&_*]:text-ink-2">
                    <MemoizedReactMarkdown
                      remarkPlugins={[remarkGfm, remarkMath, supersub]}
                      rehypePlugins={[rehypeRaw, rehypeSanitize, rehypeKatex]}
                      components={{
                        code({ node, className, children, ...props }) {
                          const match = /language-(\w+)/.exec(className || "");

                          return match ? (
                            <CodeBlock
                              key={`code-${node?.position?.start?.offset ?? String(children).slice(0, 64)}`}
                              language={(match && match[1]) || ""}
                              value={String(children).replace(/\n$/, "")}
                              {...props}
                            />
                          ) : (
                            <code className={className} {...props}>
                              {children}
                            </code>
                          );
                        },
                      }}
                    >
                      {reasoning as string}
                    </MemoizedReactMarkdown>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ThinkingTrace;
